import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { Extension } from "@tiptap/core";
import type { CommandProps } from "@tiptap/core";
import { VariableNode } from "./VariableNode";

// FontSize is not available as a standalone 2.x package.
// Implemented as a local extension on top of TextStyle.
const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types as string[],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.fontSize ?? null,
            renderHTML: (attributes: Record<string, unknown>) => {
              if (!attributes.fontSize) return {};
              const fs = String(attributes.fontSize);
              // Allowlist safe CSS size values only — prevents CSS injection via crafted docs
              if (!/^\d+(\.\d+)?(px|pt|em|rem)?$/.test(fs)) return {};
              return { style: `font-size: ${fs}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }: CommandProps) =>
          chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }: CommandProps) =>
          chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    } as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  },
});

type BuildExtensionsOpts = {
  placeholder?: string;
};

export function buildEditorExtensions(opts: BuildExtensionsOpts = {}) {
  return [
    StarterKit,
    Placeholder.configure({ placeholder: opts.placeholder ?? "" }),
    VariableNode,
    Underline,
    TextStyle,
    Color.configure({ types: ["textStyle"] }),
    Highlight.configure({ multicolor: true }),
    FontSize.configure({ types: ["textStyle"] }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
      defaultAlignment: "right",
      alignments: ["right", "center", "left", "justify"],
    }),
    Image.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          width: {
            default: null,
            parseHTML: (el: HTMLElement) =>
              el.getAttribute("width") || el.style.width || null,
            renderHTML: (attrs: Record<string, unknown>) => {
              if (!attrs.width) return {};
              return { width: String(attrs.width) };
            },
          },
          height: {
            default: null,
            parseHTML: (el: HTMLElement) =>
              el.getAttribute("height") || el.style.height || null,
            renderHTML: (attrs: Record<string, unknown>) => {
              if (!attrs.height) return {};
              return { height: String(attrs.height) };
            },
          },
        };
      },
    }).configure({
      HTMLAttributes: {
        style: "max-width: 100%;",
      },
    }),
  ];
}
